import { css } from '@emotion/react';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button, color, Typography } from '@uniquegood/realworld-studio-design';
import { usePlanLimits } from './usePlanLimits';
import useRequest from './useRequest';
import useModalState from './useModalState';
import usePromotionModal from './usePromotionModal';
import { PromotionTriggerLocation } from '@/track';

export default function useAiCreditCheck() {
  const { appId } = useParams<AppParam>();
  const { plan } = usePlanLimits();
  const { data: creditValues } = useRequest<CommonResponseModel<RemainCreditInfoResponseModel>>(
    `/apps/${appId}/credit`
  );
  const { modal, openModal, closeModal } = useModalState();
  const { openPromotionModal } = usePromotionModal();

  const handleOpenPromotionModal = useCallback(() => {
    closeModal();
    openPromotionModal({ triggerLocation: PromotionTriggerLocation.ai_credit_insufficient_edit });
  }, [openPromotionModal, closeModal]);

  const totalCredit = creditValues
    ? creditValues.data?.freeCredit + creditValues.data?.paidCredit
    : 0;

  const checkCreditAndExecute = useCallback(
    (onSuccess: () => void, requiredCredit: number) => {
      const isInsufficientCredit = totalCredit < requiredCredit;

      if (!isInsufficientCredit) {
        onSuccess();
        return;
      }

      // 요금제별 모달 분기
      if (plan === 'Free') {
        openModal({
          title: 'AI 기능 사용에 필요한 크레딧이 부족해요',
          children: (
            <>
              <Typography as="p" type="body">
                크레딧을 지금 바로 쓰려면 요금제를 업그레이드해 주세요.
              </Typography>
              <Typography
                as="p"
                type="body"
                cssStyle={css`
                  margin-top: 24px;
                  color: #8f8f8f;
                `}
              >
                별도 충전 기능은 곧 제공될 예정이에요!
              </Typography>
            </>
          ),
          customActionButton: (
            <Button
              type="primary"
              size="large"
              onClick={handleOpenPromotionModal}
              cssStyle={css`
                background: linear-gradient(90deg, #7800ff 0%, #829bff 100%);
                color: ${color.white};
                width: 152px;
                height: 44px;
                border: 0;
              `}
            >
              요금제 업그레이드
            </Button>
          )
        });
      } else if (plan === 'Pro') {
        // pro, enterprise, school 등 유료 요금제
        openModal({
          title: 'AI 기능 사용에 필요한 크레딧이 부족해요',
          children: (
            <>
              <Typography as="p" type="body">
                크레딧은 매월 결제일에 자동 충전됩니다.
              </Typography>
              <Typography
                as="p"
                type="body"
                cssStyle={css`
                  margin-top: 24px;
                  color: #8f8f8f;
                `}
              >
                별도 충전 기능은 곧 제공될 예정이에요!
              </Typography>
            </>
          ),
          customActionButton: (
            <>
              <Button
                type="basic"
                size="large"
                onClick={closeModal}
                cssStyle={css`
                  margin-right: 8px;
                `}
              >
                확인
              </Button>
              <Button
                type="primary"
                size="large"
                onClick={handleOpenPromotionModal}
                cssStyle={css`
                  background: linear-gradient(90deg, #7800ff 0%, #829bff 100%);
                  color: ${color.white};
                  width: 152px;
                  height: 44px;
                  border: 0;
                `}
              >
                요금제 자세히 보기
              </Button>
            </>
          )
        });
      } else {
        openModal({
          title: 'AI 기능 사용에 필요한 크레딧이 부족해요',
          children: (
            <>
              <Typography as="p" type="body">
                크레딧은 매월 요금제가 적용된 일자에 자동 충전됩니다.
              </Typography>
              <Typography
                as="p"
                type="body"
                cssStyle={css`
                  margin-top: 24px;
                  color: #8f8f8f;
                `}
              >
                별도 충전 기능은 곧 제공될 예정이에요!
              </Typography>
            </>
          ),
          secondaryAction: {
            content: '확인',
            onAction: closeModal
          }
        });
      }
    },
    [plan, openModal, closeModal]
  );

  return {
    checkCreditAndExecute,
    modal,
    closeModal,
    totalCredit
  };
}
